import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPreSignedUrlForDownload,
  getPreSignedUrlForUpload,
} from "../../api/admin";
import { DocumentIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { v4 as uuid } from "uuid";
import { toast } from "react-toastify";
import { DeviceModel } from "./AddDeviceToLocationModal";

const DeviceImage = ({
  deviceModel,
  setDeviceModel,
}: {
  deviceModel: DeviceModel;
  setDeviceModel: React.Dispatch<React.SetStateAction<DeviceModel>>;
}) => {
  const [imageUrl, setImageUrl] = useState<string>();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [isDragging, setIsDragging] = useState(false);
  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const handleButtonClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  const uploadFile = useCallback(
    async (file: File) => {
      const localUrl = URL.createObjectURL(file);
      setPreviewUrl(localUrl);

      try {
        const deviceId = uuid();
        const res = await getPreSignedUrlForUpload({
          contentType: file.type,
          key: `${deviceId}/image/${file.name}`,
        });
        const uploadResponse = await fetch(res.url, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        // Only persist the key once the object is actually in storage — a failed
        // PUT (e.g. bucket CORS misconfigured) otherwise leaves a dangling key
        // that renders as a broken image later.
        if (!uploadResponse.ok) {
          throw new Error(`Upload failed with status ${uploadResponse.status}`);
        }
        setDeviceModel({ ...deviceModel, deviceId, imageKey: res.key });
      } catch (err) {
        console.error("Device image upload failed:", err);
        toast.error("Failed to upload image. Please try again.");
        URL.revokeObjectURL(localUrl);
        setPreviewUrl(undefined);
      }
    },
    [deviceModel, setDeviceModel]
  );

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith("image/")) uploadFile(file);
    },
    [uploadFile]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleRemoveImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(undefined);
    setImageUrl(undefined);
    setDeviceModel({ ...deviceModel, imageKey: "" });
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!deviceModel.imageKey) {
      setImageUrl(undefined);
      return;
    }
    const fetchUrl = async () => {
      const res = await getPreSignedUrlForDownload({
        key: deviceModel.imageKey,
      });
      setImageUrl(res.url);
    };

    fetchUrl();
  }, [deviceModel.imageKey]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const displayUrl = previewUrl || imageUrl;

  return (
    <div className="w-full">
      <input
        ref={inputFileRef}
        className="hidden"
        type="file"
        accept="image/*"
        onChange={(e) => handleImageChange(e)}
      />
      {displayUrl ? (
        <div className="relative w-full rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
          <img
            className="w-full h-48 object-cover"
            src={displayUrl}
            alt="device-preview"
          />
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              type="button"
              onClick={handleButtonClick}
              className="rounded-md bg-white dark:bg-gray-700 cursor-pointer px-2 py-1 text-xs font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Change
            </button>
            <button
              type="button"
              onClick={handleRemoveImage}
              className="rounded-md bg-white dark:bg-gray-700 cursor-pointer p-1 text-gray-500 dark:text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 hover:text-red-500"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={handleButtonClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`w-full rounded-lg border border-gray-200 dark:border-gray-600 flex flex-col items-center justify-center cursor-pointer transition-colors py-8 ${
            isDragging
              ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20"
              : "hover:border-gray-300 dark:hover:border-gray-500"
          }`}
        >
          <DocumentIcon className="w-10 h-10 text-gray-400 dark:text-gray-500 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Drag and drop or{" "}
            <span className="font-semibold text-indigo-600 dark:text-indigo-400">
              choose file(s)
            </span>{" "}
            to upload
          </p>
        </div>
      )}
    </div>
  );
};

export default DeviceImage;
